import { isWeb } from '@gluestack-ui/nativewind-utils/IsWeb';
import { zodResolver } from '@hookform/resolvers/zod';
import { Image } from 'expo-image';
import { useFocusEffect, useLocalSearchParams, useRouter } from 'expo-router';
import { AlertCircleIcon } from 'lucide-react-native';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { AccessibilityInfo, findNodeHandle } from 'react-native';
import { KeyboardAwareScrollView } from 'react-native-keyboard-controller';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { z } from 'zod';

import { oauth_google } from '@/assets/images';
import { Header } from '@/components/header';
import { Heading } from '@/components/heading';
import { Button, ButtonSpinner, ButtonText } from '@/components/ui/button';
import {
  FormControl,
  FormControlError,
  FormControlErrorIcon,
  FormControlErrorText,
  FormControlLabel,
  FormControlLabelText,
} from '@/components/ui/form-control';
import { HStack } from '@/components/ui/hstack';
import { Input, InputField } from '@/components/ui/input';
import { Text } from '@/components/ui/text';
import { VStack } from '@/components/ui/vstack';
import UnauthenticatedLayout from '@/components/unauthenticatedLayout';
import { ROUTES } from '@/constants/routes';
import { useBackNavigation } from '@/hooks/useBackNavigation';
import { useColors } from '@/hooks/useColors';
import { useRedirectPath } from '@/hooks/useRedirectPath';
import { useToaster } from '@/hooks/useToaster';
import { startGoogleSignIn } from '@/services/auth';
import { checkEmailAvailable } from '@/services/checkEmailAvailable';
import { requestMagicLink } from '@/services/requestMagicLink';
import { getAccessibilityProps } from '@/util/accessibilityProps';
import {
  trackAuthTriggered,
  trackEmailSigninLinkFormSubmitted,
  trackEmailSigninLinkViewed,
} from '@/util/analyticsEvents';
import { Sentry } from '@/util/sentry';

const schema = z.object({
  email: z.string().min(1, 'Email is required').email({
    message: 'Invalid email address',
  }),
});

function Content() {
  const params = useLocalSearchParams<{ email?: string }>();

  const initialEmail = typeof params.email === 'string' ? params.email.trim().toLowerCase() : '';

  const {
    control,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm({
    defaultValues: {
      email: initialEmail,
    },
    resolver: zodResolver(schema),
  });

  const toast = useToaster();

  const { replace } = useRouter();

  const { redirectPath, appendRedirectPath } = useRedirectPath();

  const [googleHint, setGoogleHint] = useState<{
    email: string;
    maskedEmail?: string;
  } | null>(null);

  const googleButtonRef = useRef<React.ElementRef<typeof Button>>(null);

  useEffect(() => {
    if (!googleHint) {
      return;
    }

    if (isWeb) {
      (googleButtonRef.current as unknown as HTMLElement | null)?.focus();
    } else {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const node = findNodeHandle(googleButtonRef.current as any);

      if (node) {
        AccessibilityInfo.setAccessibilityFocus(node);
      }
    }
  }, [googleHint]);

  useFocusEffect(
    useCallback(() => {
      trackEmailSigninLinkViewed();
    }, [])
  );

  const onSubmit = handleSubmit(async ({ email }) => {
    try {
      trackEmailSigninLinkFormSubmitted(email);

      let isGoogleAccount = false;

      let maskedEmail: string | undefined;

      try {
        const availability = await checkEmailAvailable(email);

        isGoogleAccount = availability.authProvider === 'google';

        maskedEmail = availability.maskedEmail;
      } catch (lookupError) {
        // Fail-open: a transient availability error shouldn't block sending the link.
        Sentry.addBreadcrumb({
          category: 'auth',
          message: 'checkEmailAvailable failed, falling through to magic link send',
          level: 'warning',
          data: {
            error: lookupError instanceof Error ? lookupError.message : String(lookupError),
          },
        });
      }

      if (isGoogleAccount) {
        setGoogleHint({ email, maskedEmail });

        return;
      }

      await requestMagicLink(email);

      replace(appendRedirectPath(`/email-link/success?email=${encodeURIComponent(email)}`));
    } catch (error) {
      console.error('RequestMagicLink ERROR:', error);

      toast.showError({
        title: 'There was an issue with your submission. Please try again.',
      });
    }
  });

  const handleContinueWithGoogle = () => {
    if (!googleHint) {
      return;
    }

    trackAuthTriggered({ provider: 'google', source: 'email_link_screen' });

    startGoogleSignIn('email_link_screen', {
      loginHint: googleHint.email,
      ...(redirectPath ? { redirectPath } : {}),
    });
  };

  const handleUseDifferentEmail = () => {
    setGoogleHint(null);
  };

  const { colors } = useColors();

  const { bottom } = useSafeAreaInsets();

  return (
    <VStack
      className="flex-1"
      style={{
        paddingBottom: bottom,
      }}>
      <Header
        contentScrollsUnderHeader
        headerShown={!isWeb}
        isBackButton
        backRoute={ROUTES.LOGIN}
        title="Email a signin link"
        statusBarBackgroundColor={colors.background[0]}
      />
      <KeyboardAwareScrollView
        contentInsetAdjustmentBehavior="automatic"
        mode="layout"
        bottomOffset={12}
        keyboardShouldPersistTaps="handled"
        keyboardDismissMode="on-drag"
        contentContainerClassName="flex-col flex-grow px-5 py-6">
        <VStack className="flex-1 web:mb-8">
          {isWeb ? (
            <Heading size="h1-xl" className="mb-2">
              Email a sign-in link
            </Heading>
          ) : null}
          <Text className="mb-8">
            Enter your email address and we'll send you a link to sign in.
          </Text>
          <Controller
            name="email"
            control={control}
            render={({ field: { onChange, value } }) => (
              <FormControl isInvalid={!!errors.email?.message}>
                <FormControlLabel className="mb-2">
                  <FormControlLabelText>Email*</FormControlLabelText>
                </FormControlLabel>
                <Input>
                  <InputField
                    autoCorrect={false}
                    placeholder="Enter email address"
                    placeholderTextColor={colors.typography[600]}
                    style={{ color: colors.typography[900] }}
                    className="text-base"
                    value={value}
                    onChangeText={(text: string) => {
                      onChange(text.toLowerCase());

                      if (googleHint && text.toLowerCase() !== googleHint.email) {
                        setGoogleHint(null);
                      }
                    }}
                    keyboardType="email-address"
                    autoCapitalize="none"
                    enterKeyHint="done"
                    onSubmitEditing={onSubmit}
                  />
                </Input>
                <FormControlError className="mt-2">
                  <FormControlErrorIcon size="xs" as={AlertCircleIcon} />
                  <FormControlErrorText size="xs">{errors.email?.message}</FormControlErrorText>
                </FormControlError>
              </FormControl>
            )}
          />

          {googleHint ? (
            <VStack
              className="mt-6 gap-3 rounded-md bg-background-info px-4 py-4"
              {...(isWeb ? { 'aria-live': 'polite' } : { accessibilityLiveRegion: 'polite' })}
              {...getAccessibilityProps({ role: 'article', label: 'Sign in with Google instead' })}>
              <Text size="sm" className="font-semibold text-typography-900">
                This account uses Google sign-in
              </Text>
              <Text size="xs" className="text-typography-700">
                {googleHint.maskedEmail
                  ? `${googleHint.maskedEmail} signed up with Google, so we can't email a sign-in link. Continue with Google to sign in.`
                  : "This account signed up with Google, so we can't email a sign-in link. Continue with Google to sign in."}
              </Text>
              <Button
                ref={googleButtonRef}
                onPress={handleContinueWithGoogle}
                variant="outline"
                className="flex-row items-center gap-x-3">
                <Image
                  source={oauth_google}
                  style={{ width: 20, height: 20 }}
                  contentFit="contain"
                  aria-hidden
                  accessible={false}
                />
                <ButtonText>Continue with Google</ButtonText>
              </Button>
              <HStack className="justify-center">
                <Button variant="link" onPress={handleUseDifferentEmail}>
                  <ButtonText size="xs" className="text-primary-500">
                    Use a different email
                  </ButtonText>
                </Button>
              </HStack>
            </VStack>
          ) : null}
        </VStack>
        {googleHint ? null : (
          <Button className="gap-x-2" onPress={onSubmit} disabled={isSubmitting}>
            {isSubmitting ? <ButtonSpinner color={colors.primary[200]} /> : null}
            <ButtonText>Email link</ButtonText>
          </Button>
        )}
      </KeyboardAwareScrollView>
    </VStack>
  );
}

export default function EmailLink() {
  const { appendRedirectPath } = useRedirectPath();

  const backPath = appendRedirectPath(ROUTES.LOGIN);

  const { navigateBack } = useBackNavigation(backPath);

  if (isWeb) {
    return (
      <UnauthenticatedLayout onBack={navigateBack}>
        <Content />
      </UnauthenticatedLayout>
    );
  }

  return <Content />;
}
